<template>
  <DxChart
    id="chart"
    :data-source="histogramFormatted"
    :customize-point="customizePoint"
    :bar-group-width="100"
    palette="Material"
  >
    <DxTitle :text="''" />
    <DxSeries
      argument-field="x"
      value-field="y"
      :name="`${index} - ${formattedShowingDate}`"
      :show-in-legend="false"
      color="#fff"
      type="bar"
    />
    <DxSeries
      v-for="(serie, index) in getChartSeries"
      :key="index"
      :value-field="serie.value"
      :name="serie.class"
      :color="getSeriesColor(index)"
    />
    <DxTooltip
      :enabled="true"
      :z-index="999999"
      :customize-tooltip="customizeTooltip"
    />
    <DxScrollBar v-if="!isForPdf" :visible="true" />
    <DxZoomAndPan v-if="!isForPdf" argument-axis="both" />
    <DxLegend
      vertical-alignment="bottom"
      horizontal-alignment="center"
      item-text-position="right"
      :font="isForPdf ? { size: 16, weight: 500 } : {}"
    />
    <DxArgumentAxis :tick-interval=xThicks>
      <DxTick :visible="true" />
    </DxArgumentAxis>
    <DxSize v-if="isForPdf" :height="300" />
  </DxChart>
</template>

<script>
import {
  DxChart, DxSeries, DxTooltip, DxScrollBar, DxZoomAndPan, DxLegend, DxTitle, DxArgumentAxis, DxTick, DxSize,
} from 'devextreme-vue/chart';
import moment from 'moment';

export default {
  name: 'HistogramGraph',
  components: {
    DxChart,
    DxSeries,
    DxTooltip,
    DxScrollBar,
    DxZoomAndPan,
    DxLegend,
    DxTitle,
    DxArgumentAxis,
    DxTick,
    DxSize,
  },
  props: {
    histogram: {
      type: Object,
      default: () => ({}),
    },
    showingDate: {
      type: String,
      default: '',
    },
    index: {
      type: String,
      default: '',
    },
    max: {
      type: Number,
      default: null,
    },
    min: {
      type: Number,
      default: null,
    },
    mean: {
      type: Number,
      default: null,
    },
    stddev: {
      type: Number,
      default: null,
    },
    isForPdf: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      colors: ['#f1722b', '#fdae61', '#ffffbf', '#a6d96a', '#1a9641'],
      colorsNDMI: ['#d7191c','#fdae61','#ffffbf','#3db5dd','#2b83ba'],
      colorsGLI: ['#5a9542', '#306637', '#19552b', '#104539', '#0b393e'],
      colorsNDVICONTRASTADO: ['#e75437', '#febe74', '#b8e17b', '#74b4fe', '#1674e1'],
      xThicks: 0.1,
      indexLimits: {
        NDVI: {
          max: 1,
          min: 0,
        },
        NDVI_CONT: {
          max: this.max,
          min: this.min,
        },
        SAVI: {
          max: 1,
          min: 0,
        },
        ARVI: {
          max: 1,
          min: 0,
        },
        VARI: {
          max: 1,
          min: -0.6,
        },
        LAI: {
          max: 1,
          min: 0,
        },
        NDRE: {
          max: 1,
          min: 0,
        },
        RECI: {
          max: 1,
          min: -0.6,
        },
        NDMI: {
          max: 1,
          min: -1,
        },
        GNDVI: {
          max: 1,
          min: 0,
        },
        GLI: {
          max: 1,
          min: -1,
        },
        TCI: {
          max: 1,
          min: -1,
        },
        MSAVI: {
          max: 1,
          min: -0.8,
        },
      }
    };
  },
  computed: {
    histogramFormatted() {
      const auxHistogramFormatted = [];
      const { hist_X, hist_Y } = this.histogram;
      const { indexLimits, index } = this;
      const indexLimit = indexLimits[index];

      // Declarar límites ajustados
      let adjustedMin = indexLimit.min;
      let adjustedMax = indexLimit.max;
      this.xThicks = 0.1;

      // Si el índice es NDVI_CONT, calcular límites ajustados dinámicamente
      if (index === 'NDVI_CONT') {
        const firstNonZeroIndex = hist_Y.findIndex(y => y > 0);
        this.xThicks = 0.05;

        // Si no se encuentra ninguna posición válida, retornar un array vacío
        if (firstNonZeroIndex === -1) {
          return auxHistogramFormatted;
        }

        adjustedMin = Math.min(indexLimit.min, hist_X[firstNonZeroIndex]) - this.xThicks; // Asegurar incluir el primer dato válido menos un xThicks
        adjustedMax = Math.min(indexLimit.max + this.xThicks, 1) ; // Máximo permitido es 1

      }

      // Iterar sobre los valores para construir el histograma formateado
      for (let i = 0; i < hist_X.length; i += 1) {
        const x = hist_X[i];
        const y = hist_Y[i];

        // Verificar si el valor x está dentro de los límites
        const withinLimits =
          index === 'NDVI_CONT'
            ? x >= adjustedMin && x < adjustedMax // Usar límites ajustados para NDVI_CONT
            : x > indexLimit.min && x < indexLimit.max; // Usar límites normales para otros índices
  
        if (withinLimits) {
          const { color } = this.getClassAndColor(x); // Obtenemos la clase y color
          auxHistogramFormatted.push({
            x,
            y: y / 100, // Normalizamos y
            color,
          });
        }
      }
      return auxHistogramFormatted;
    },
    formattedShowingDate() {
      return moment(this.showingDate).format('DD-MM-YYYY');
    },
    getChartSeries() {
      const formatted = this.histogramFormatted.map((item) => ({
        ...item,
        class: this.getClassAndColor(item.x).class,
      }));

      const groupedByClass = formatted.reduce((acc, item) => ({
        ...acc,
        [item.class]: (acc[item.class] || 0) + item.y,
      }), {});

      // Obtener las claves (clases) y ordenarlas
      const sortedClasses = Object.keys(groupedByClass).sort((a, b) => {
        const lowerLimitA = parseFloat(a.split(' - ')[0]);
        const lowerLimitB = parseFloat(b.split(' - ')[0]);
        return lowerLimitA - lowerLimitB; // Ordenar de menor a mayor
      });
      // Convertimos al formato esperado (string)
      return sortedClasses.map(className => ({
        class: className,
        value: groupedByClass[className].toFixed(2), // Pasamos 2 decimales
      }));
    },
  },
  watch: {
    // Para actualizar el maximo y minimo del indice
    max(newValue, oldValue) {
      if (newValue !== oldValue && this.index === 'NDVI_CONT') {
        this.updateIndexLimits();
      }
    },
    min(newValue, oldValue) {
      if (newValue !== oldValue && this.index === 'NDVI_CONT') {
        this.updateIndexLimits();
      }
    },
    // Para ejecutarlo cuando cambie el indice ya que en el caso de ndvi a ndvi_cont tiene los mismos datos
    // y no los detectaria solo al cambiar los valores max/min
    index(newValue, oldValue) {
      if (newValue !== oldValue && this.index === 'NDVI_CONT') {
        this.updateIndexLimits();
      }
    },
  },
  methods: {
    // Funcion para actualizar maximo y minimos para el NDVI CONTRASTADO
    updateIndexLimits() {
      // Verificamos que el indice existe
      if (this.indexLimits[this.index]) {
        this.indexLimits[this.index].max = this.max;
        this.indexLimits[this.index].min = this.min;
      }
    },
    customizeTooltip(pointInfo) {
      let classRange = parseInt(this.histogram.hist_X.length / 5);
      let classType = this.getClassAndColor(pointInfo.argument.toFixed(2), classRange);
      return {
        html: `
          <span class='bold'>${this.$t('class')}: ${classType.class}</span><br>
          <span class='bold'>${this.$t('parcel.detail.area')}: ${pointInfo.value} ha</span>
        `,
      };
    },
    getClassAndColor(value) {
      if (this.index === 'NDVI_CONT') {
        return this.getNDVIContClassAndColor(value);
      } else {
        const { max, min } = this.indexLimits[this.index];
        const range = Math.abs(max - min) / 5;
        const thresholds = Array.from({ length: 6 }, (_, i) => min + i * range);

        const index = thresholds.findIndex((threshold, i) => value < threshold && value >= thresholds[i - 1]) - 1;
        const classLabel = `${thresholds[index].toFixed(2)} - ${thresholds[index + 1].toFixed(2)}`;
        const colorArray = this.index === 'NDMI' ? this.colorsNDMI : (this.index === 'GLI' ? this.colorsGLI : this.colors);
        
        return { class: classLabel, color: colorArray[index] };
     }
    },
    getNDVIContClassAndColor(value) {
      const { mean, min, max } = this;

      // Verificar si break_color está disponible
      if (this.histogram.break_color && this.histogram.break_color.length > 0) {
        const breakPoints = this.histogram.break_color;
        for (let i = 0; i < breakPoints.length - 1; i++) {
          const lowerLimit = breakPoints[i];
          const upperLimit = breakPoints[i + 1];

          if (value >= lowerLimit && value < upperLimit) {
            return { class: `${lowerLimit.toFixed(2)} - ${upperLimit.toFixed(2)}`, color: this.colorsNDVICONTRASTADO[i] };
          }
          // Si el valor esta por debajo del lowerLimit lo metemos en el primer rango
          else if (value < lowerLimit) {
            return { class: `${breakPoints[0].toFixed(2)} - ${breakPoints[1].toFixed(2)}`, color: this.colorsNDVICONTRASTADO[0] };
          }
        }

        // Si el valor es igual o mayor al último límite, devuelve la última clase
        return { class: `${breakPoints[breakPoints.length - 2].toFixed(2)} - ${breakPoints[breakPoints.length - 1].toFixed(2)}`, color: this.colorsNDVICONTRASTADO[breakPoints.length - 2] };
      }

      // Lógica existente para el caso sin break_color
      const filt_hist = this.histogram.hist_X.map((x, i) => ({ x, y: this.histogram.hist_Y[i] })).filter((d) => d.x >= 0 && d.x <= 1);

      // Conseguir el valor x del bin mínimo
      const min_h = filt_hist.find((d) => d.y > 0)?.x;

      // Conseguir el valor x del bin máximo
      const max_h = [...filt_hist].reverse().find((d) => d.y > 0)?.x;

      const j_min = (mean - min) / 4;
      const j_max = (max - mean) / 4;

      const n1 = min_h + j_min;
      const n2 = min_h + 3 * j_min;
      const n3 = max_h - 3 * j_max;
      const n4 = max_h - j_max;

      if (value <= n1) {
        return { class: `0 - ${n1.toFixed(2)}`, color: '#e75437' };
      } else if (value <= n2) {
        return { class: `${n1.toFixed(2)} - ${n2.toFixed(2)}`, color: '#febe74' };
      } else if (value <= n3) {
        return { class: `${n2.toFixed(2)} - ${n3.toFixed(2)}`, color: '#b8e17b' };
      } else if (value <= n4) {
        return { class: `${n3.toFixed(2)} - ${n4.toFixed(2)}`, color: '#74b4fe' };
      } else {
        return { class: `${n4.toFixed(2)} - 1`, color: '#1674e1' };
      }
    },

    customizePoint({ data }) {
      return { color: data.color, hoverStyle: { color: data.color } };
    },
    getSeriesColor(index) {
      const colorArray = this.index === 'NDVI_CONT' ? this.colorsNDVICONTRASTADO : (this.index === 'NDMI' ? this.colorsNDMI : (this.index === 'GLI' ? this.colorsGLI : this.colors));
      return colorArray[index];
    },
  },
};
</script>

<style lang="scss" scoped>
@import './HistogramGraph.styles.scss';
</style>